import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Camera, Send, FileText, MessageSquare, Image as ImageIcon,
  Calendar, Loader2, AlertCircle, CheckCircle, UserPlus, BarChart3,
  LayoutDashboard, ClipboardList, Flag, DollarSign, FileBox,
  Paperclip, X, Download, Landmark, Gauge,
} from "lucide-react";
import PortalLayout from "../../components/portal/PortalLayout";
import { portalApi, compressImage } from "../../lib/portalApi";
import { usePortalAuth } from "../../contexts/PortalAuthContext";
import { usePortalLang } from "../../lib/PortalLanguageContext";
import { TimelineTab } from "../../components/portal/TimelineTab";
import CommissioningTab from "../../components/portal/CommissioningTab";
import { PreviewAsButton } from "../../components/portal/PreviewAsControls";
import {
  DailyUpdateCard, PhotosTab, ReportsTab, MobileQuickActions, TranslatedText,
} from "../../components/portal/ProjectTabs";
import { useContentTranslation } from "../../lib/useContentTranslation";
import { MilestonesTab } from "../../components/portal/MilestonesTab";
import { FinancialsTab } from "../../components/portal/FinancialsTab";
import FinanceLedgerSection from "../../components/portal/FinanceLedgerSection";
import { KeyDocumentsTab, KeyDocumentsCard } from "../../components/portal/KeyDocumentsTab";
import EditDailyUpdateModal from "../../components/portal/EditDailyUpdateModal";
import ProgressSummaryCard from "../../components/portal/ProgressSummaryCard";
import DemolitionCorrectionControl from "../../components/portal/DemolitionCorrectionControl";
import DemolitionScopeCard from "../../components/portal/DemolitionScopeCard";
import ApprovalsTab from "../../components/portal/ApprovalsTab";
import EditProjectModal from "../../components/portal/EditProjectModal";

const STATUS_HERO_LABEL = {
  planning: "Planning", active: "Active", delayed: "Delayed",
  awaiting_client: "Awaiting client", completed: "Completed", handover: "Handover",
};
const STATUS_HERO_COLOR = {
  planning: "bg-[#7A6B5F] text-[#FAF5EC]",
  active: "bg-[#6E1F2C] text-[#FAF5EC]",
  delayed: "bg-[#A3141A] text-[#FAF5EC]",
  awaiting_client: "bg-[#C8A75D] text-[#1C110C]",
  completed: "bg-[#3F6E2C] text-[#FAF5EC]",
  handover: "bg-[#1C110C] text-[#FAF5EC]",
};

function ProgressRing({ pct, size = 120 }) {
  const { t } = usePortalLang();
  const v = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const r = (size - 16) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - v / 100);
  const half = size / 2;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`ring-grad-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#D4A963" />
            <stop offset="60%" stopColor="#A6822F" />
            <stop offset="100%" stopColor="#6E1F2C" />
          </linearGradient>
        </defs>
        <circle cx={half} cy={half} r={r} stroke="#F3E9D2" strokeWidth="9" fill="none" />
        <circle
          cx={half} cy={half} r={r}
          stroke={`url(#ring-grad-${size})`} strokeWidth="9" fill="none"
          strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${half} ${half})`}
          style={{ transition: "stroke-dashoffset 600ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-serif-display text-3xl text-[#1C110C] leading-none">{v}%</span>
        <span className="text-[8px] tracking-[0.3em] uppercase text-[#7A6B5F] mt-1">{t("hero.complete")}</span>
      </div>
    </div>
  );
}

const WS_CHIP = {
  completed: "bg-emerald-600/15 text-emerald-700",
  in_progress: "bg-[#6E1F2C]/10 text-[#6E1F2C]",
  ongoing: "bg-[#A6822F]/15 text-[#A6822F]",
  scheduled: "bg-[#A6822F]/10 text-[#8A6A20]",
  pending: "bg-[#7A6B5F]/15 text-[#7A6B5F]",
  not_tracked: "bg-[#7A6B5F]/10 text-[#7A6B5F]",
};

function WorkstreamDebugModal({ slug, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    portalApi.workstreamsDebug(slug).then(setD).catch((e) => setErr(e.message));
  }, [slug]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="portal-ws-debug-modal">
      <div className="bg-white w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-lg text-[#1C110C]">Workstream calculation debug</h3>
          <button type="button" onClick={onClose} data-testid="portal-ws-debug-close" className="text-[#6E1F2C]"><X size={18} /></button>
        </div>
        {err && <p className="text-xs text-[#A3141A]">{err}</p>}
        {!d && !err && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
        {d && (
          <div className="space-y-4 text-[11px] text-[#3E2A22]">
            <p className="bg-[#FAF5EC] border-l-2 border-[#A6822F] px-3 py-2">{d.exclusions}</p>
            <div>
              <p className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1">Workstream date sources</p>
              {d.workstreams.map((w) => (
                <div key={w.key} className="flex flex-wrap gap-x-2 border-b border-[#6E1F2C]/10 py-1.5" data-testid={`portal-ws-debug-${w.key}`}>
                  <b className="text-[#1C110C]">{w.label}:</b> {w.status_label}
                  {w.date_source
                    ? <span className="text-[#7A6B5F]">· date from task “{w.date_source.name}” ({w.date_source.start_date}) · id {w.date_source.task_id}</span>
                    : <span className="text-[#7A6B5F]">· {w.detail || "no dated open task"}</span>}
                </div>
              ))}
            </div>
            <p><b>Next milestone:</b> {d.next_milestone ? `${d.next_milestone.name} (${d.next_milestone.target_date || "no date"})${d.next_milestone.note ? ` — ${d.next_milestone.note}` : ""}` : "—"}</p>
            <p><b>Next site activity:</b> {d.next_site_activity ? `${d.next_site_activity.name} (${d.next_site_activity.start_date || d.next_site_activity.note || "—"})` : "—"}</p>
            <div>
              <p className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F] mb-1">Site tasks considered ({d.site_tasks_considered.length})</p>
              {d.site_tasks_considered.map((t) => (
                <p key={t.id} className="py-0.5">{t.name} — <span className="text-[#7A6B5F]">{t.workstream} · {t.status} · {t.percent}% · {t.start_date || "no start"} → {t.finish_date || "—"}</span></p>
              ))}
            </div>
            {d.duplicates_excluded.length > 0 && (
              <div>
                <p className="text-[9px] tracking-[0.24em] uppercase text-[#A3141A] mb-1">Duplicate rows excluded ({d.duplicates_excluded.length})</p>
                {d.duplicates_excluded.map((t) => (
                  <p key={t.id} className="py-0.5 text-[#7A6B5F]">{t.name} — {t.status} · {t.start_date || "no start"}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function WorkstreamStatusPanel({ ws, slug, canManage, isAdmin }) {
  const { t } = usePortalLang();
  const [debugOpen, setDebugOpen] = useState(false);
  if (!ws || !ws.workstreams?.length) return null;
  const items = ws.workstreams.filter((w) => w.status !== "not_tracked");
  if (!items.length) return null;
  return (
    <div className="mt-7 pt-6 border-t border-[#6E1F2C]/10" data-testid="portal-workstreams-panel">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium mb-1">
          {t("hero.statusByWorkstream", "Project Status by Workstream")}
        </p>
        {canManage && (
          <button type="button" onClick={() => setDebugOpen(true)} data-testid="portal-ws-debug-btn"
            className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] hover:text-[#6E1F2C] transition-colors">
            Calculation debug
          </button>
        )}
      </div>
      <p className="text-[10px] text-[#7A6B5F] mb-3">
        {t("hero.workstreamsNote", "Approvals, procurement and site works run in parallel — procurement does not hold up site activities.")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((w) => (
          <div key={w.key} className="flex items-center justify-between gap-2 border border-[#6E1F2C]/10 bg-white/60 px-3 py-2" data-testid={`portal-workstream-${w.key}`}>
            <div className="min-w-0">
              <p className="text-[11px] text-[#1C110C] truncate">{w.label}</p>
              {w.detail && <p className="text-[9px] text-[#7A6B5F] truncate">{w.detail}</p>}
            </div>
            <span className={`shrink-0 text-[9px] tracking-[0.14em] uppercase px-2 py-1 ${WS_CHIP[w.status] || WS_CHIP.pending}`} data-testid={`portal-workstream-status-${w.key}`}>
              {w.status_label}{w.percent != null && w.status === "in_progress" ? ` · ${w.percent}%` : ""}
            </span>
          </div>
        ))}
      </div>
      {isAdmin && <DemolitionCorrectionControl slug={slug} isAdmin={isAdmin} />}
      <DemolitionScopeCard slug={slug} isAdmin={isAdmin} canEdit={isAdmin} />
      {debugOpen && <WorkstreamDebugModal slug={slug} onClose={() => setDebugOpen(false)} />}
    </div>
  );
}

function PhaseStepper({ phases, currentId }) {
  const { t } = usePortalLang();
  if (!phases || !phases.length) return null;
  return (
    <div className="mt-7 pt-6 border-t border-[#6E1F2C]/10">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium">
          {t("hero.projectWorkstreams", "Project Workstreams")}
        </p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
          {t(phases.length === 1 ? "hero.stage.one" : "hero.stage.other", null, { n: phases.length })}
        </p>
      </div>
      <div className="relative">
        {/* Connector line */}
        <div className="absolute left-0 right-0 top-3 h-0.5 bg-[#F3E9D2]" />
        <div
          className="absolute left-0 top-3 h-0.5 bg-gradient-to-r from-[#D4A963] to-[#6E1F2C] transition-all duration-500"
          style={{
            width: (() => {
              const totalCompletion = phases.reduce((sum, p) => sum + (p.progress_percent || 0), 0);
              return `${Math.min(100, (totalCompletion / (phases.length * 100)) * 100)}%`;
            })(),
          }}
        />
        <div className={`relative grid gap-2`} style={{ gridTemplateColumns: `repeat(${phases.length}, minmax(0, 1fr))` }}>
          {phases.map((p, i) => {
            const done = (p.progress_percent || 0) >= 100;
            const isCurrent = currentId && p.id === currentId;
            return (
              <div key={p.id || i} className="flex flex-col items-center text-center">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border-2 ${
                    done
                      ? "bg-[#6E1F2C] border-[#6E1F2C] text-[#FAF5EC]"
                      : isCurrent
                      ? "bg-[#FAF5EC] border-[#D4A963] text-[#6E1F2C] shadow-[0_0_0_4px_rgba(212,169,99,0.25)]"
                      : "bg-white border-[#7A6B5F]/30 text-[#7A6B5F]"
                  }`}
                  title={p.name}
                >
                  {done ? <CheckCircle size={12} /> : i + 1}
                </div>
                <p
                  className={`text-[9px] mt-2 line-clamp-2 leading-tight tracking-wide ${
                    isCurrent ? "text-[#6E1F2C] font-semibold" : done ? "text-[#1C110C]" : "text-[#7A6B5F]"
                  }`}
                >
                  {p.name}
                </p>
                {p.demolition_display ? (
                  <p className="text-[8px] text-[#6E1F2C] tracking-wider mt-0.5" data-testid={`portal-stepper-demo-${p.id}`}>
                    {p.demolition_display.percent != null
                      ? `${p.demolition_display.percent}% measured`
                      : "aggregate unresolved"}
                  </p>
                ) : (p.progress_percent > 0 && p.progress_percent < 100 && (
                  <p className="text-[8px] text-[#7A6B5F] tracking-wider mt-0.5">{p.progress_percent}%</p>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HeroStat({ label, value, sub, accent, testid }) {
  const colors = {
    maroon: { val: "text-[#6E1F2C]", border: "border-[#6E1F2C]/15" },
    gold: { val: "text-[#A6822F]", border: "border-[#D4A963]/30" },
    red: { val: "text-[#A3141A]", border: "border-[#A3141A]/25" },
    charcoal: { val: "text-[#1C110C]", border: "border-[#1C110C]/15" },
  }[accent] || { val: "text-[#1C110C]", border: "border-[#6E1F2C]/15" };
  return (
    <div data-testid={testid} className={`bg-[#FAF5EC]/70 border ${colors.border} px-4 py-3.5`}>
      <p className="text-[9px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-1.5 font-medium">{label}</p>
      <p className={`font-serif-display text-lg leading-tight ${colors.val} line-clamp-1`}>{value || "—"}</p>
      {sub && <p className="text-[10px] text-[#3E2A22] mt-1 line-clamp-1">{sub}</p>}
    </div>
  );
}

function ProgressBreakdownModal({ slug, onClose }) {
  const [bd, setBd] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    portalApi.progressBreakdown(slug).then(setBd).catch((e) => setErr(e.message));
  }, [slug]);
  const METHOD_LABEL = {
    physical_weighted_tasks:
      "Physical execution tasks weighted by working days (overall = physical × 95% + authority approvals × 5%). " +
      "Approvals, payments, procurement and planning rows are excluded from physical progress.",
    milestones_value_weighted: "Weighted average of milestone progress (weighted by milestone value)",
    milestones_equal_weight: "Average of milestone progress (all milestones weigh equally)",
    latest_update: "Latest daily-update progress (no milestone progress recorded yet)",
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="portal-progress-breakdown-modal"
        className="bg-white w-full max-w-[560px] border border-[#6E1F2C]/20 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Overall progress</p>
            <h3 className="font-serif-display text-xl text-[#1C110C]">How is this calculated?</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="portal-progress-breakdown-close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {err && <p className="text-xs text-[#A3141A]">{err}</p>}
          {!bd && !err && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
          {bd && (
            <>
              <div className="flex items-baseline gap-3">
                <span className="font-serif-display text-4xl text-[#6E1F2C]" data-testid="portal-progress-breakdown-value">
                  {bd.computed_percent != null ? `${bd.computed_percent}%` : "—"}
                </span>
                <span className="text-xs text-[#3E2A22]">{METHOD_LABEL[bd.method] || "No progress data yet — add milestone progress or a daily update with a progress %."}</span>
              </div>
              {bd.milestones && bd.milestones.length > 0 && (
                <div className="border border-[#6E1F2C]/15 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAF5EC] text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F]">
                      <tr>
                        <th className="text-left px-3 py-2">Milestone</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-right px-3 py-2">Progress</th>
                        <th className="text-right px-3 py-2">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bd.milestones.map((m) => (
                        <tr key={m.id} className="border-t border-[#6E1F2C]/10">
                          <td className="px-3 py-2 text-[#1C110C]">{m.name}</td>
                          <td className="px-3 py-2 text-[#7A6B5F] capitalize">{(m.status || "planned").replace("_", " ")}</td>
                          <td className="px-3 py-2 text-right text-[#6E1F2C] font-medium">{m.progress_percent}%</td>
                          <td className="px-3 py-2 text-right text-[#7A6B5F]">{m.weight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-[#7A6B5F] leading-relaxed">
                Milestones marked <em>completed</em> count as 100%. Partial milestone progress counts proportionally —
                the project no longer stays at 0% until a milestone fully completes. Update milestone progress via a
                daily update (Link this update to… → Milestone) or from the Milestones tab.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectOverviewHero({ slug, project, refreshKey, canManage, isAdmin }) {
  const [ov, setOv] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const { t } = usePortalLang();
  useEffect(() => {
    portalApi.getOverview(slug).then(setOv).catch(() => setOv(null));
  }, [slug, refreshKey]);
  if (!ov || !project) return null;
  const p = ov.project;
  const days = ov.days_remaining;
  const pm = ov.project_manager;
  const targetDate = p.target_date ? p.target_date.slice(0, 10) : null;
  const pendingActions = ov.pending_actions || [];

  return (
    <div
      data-testid="portal-overview-hero"
      className="relative bg-gradient-to-br from-white via-white to-[#FAF5EC] border border-[#6E1F2C]/15 mb-7 overflow-hidden"
    >
      {/* Decorative gold corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#D4A963]/15 via-[#D4A963]/5 to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D4A963] via-[#A6822F] to-[#6E1F2C]" />

      <div className="relative p-6 md:p-8">
        {/* Top row: ring + status + key info */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-7 items-start">
          {/* Left — Progress ring + status */}
          <div className="flex items-center gap-5">
            <ProgressRing pct={p.progress_percent || 0} size={130} />
            <div className="space-y-2.5">
              <p className="text-[10px] tracking-[0.36em] uppercase text-[#6E1F2C] font-medium">
                {t("hero.overallProgress", "Overall Progress")}
              </p>
              <p className="text-[9px] text-[#7A6B5F] tracking-wide -mt-1.5">
                {t("hero.fullProgramme", "Weighted across the full programme")}
              </p>
              {(p.active_site_phases || []).length > 0 && (
                <p className="text-[10px] text-[#6E1F2C] font-semibold" data-testid="portal-hero-current-phase">
                  {p.active_site_phases
                    .map((a) => (a.percent != null ? `${a.label} ${a.percent}%` : (a.display || `${a.label} — aggregate unresolved`)))
                    .join(" · ")}
                </p>
              )}
              {canManage && (
                <button
                  type="button"
                  onClick={() => setShowBreakdown(true)}
                  data-testid="portal-progress-how-btn"
                  className="block text-[10px] text-[#A6822F] underline underline-offset-2 hover:text-[#6E1F2C]"
                >
                  How is this calculated?
                </button>
              )}
              <span
                className={`inline-block text-[9px] tracking-[0.24em] uppercase px-2.5 py-1 ${
                  STATUS_HERO_COLOR[p.status] || "bg-[#7A6B5F] text-white"
                }`}
              >
                {t(`status.${p.status === "awaiting_client" ? "awaitingApproval" : p.status}`, STATUS_HERO_LABEL[p.status] || p.status)}
              </span>
              {days !== null && (
                <div className="flex items-center gap-2">
                  <Calendar size={13} className={ov.overdue ? "text-[#A3141A]" : "text-[#6E1F2C]"} />
                  <p
                    className={`text-xs font-medium ${
                      ov.overdue ? "text-[#A3141A]" : days <= 14 ? "text-[#6E1F2C]" : "text-[#3E2A22]"
                    }`}
                  >
                    {ov.overdue
                      ? t("proj.card.daysOverdue", null, { n: days })
                      : t("hero.daysRemaining", null, { n: days })}
                  </p>
                </div>
              )}
              {pendingActions.length > 0 && (
                <div
                  data-testid="portal-overview-pending-action"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase bg-[#D4A963]/20 text-[#6E1F2C] border border-[#D4A963]/40 px-2 py-1"
                >
                  <AlertCircle size={11} />
                  {t("hero.pendingClientActions", null, { n: pendingActions.length })}
                </div>
              )}
            </div>
          </div>

          {/* Right — Stat tiles grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <HeroStat
              testid="portal-overview-current-phase"
              label={t("hero.primarySitePhase", "Primary Site Phase")}
              value={ov.workstreams?.primary_site_phase || ov.current_phase?.name || t("hero.programmeNotLoaded", "Programme not loaded")}
              sub={ov.workstreams?.parallel_dependency
                || (ov.workstreams?.next_site_activity ? `${t("hero.nextOnSite", "Next on site")}: ${ov.workstreams.next_site_activity.name}` : null)
                || (ov.current_phase ? `${ov.current_phase.progress_percent || 0}% ${t("hero.complete", "complete")}` : t("hero.uploadGantt", "Upload Gantt to begin"))}
              accent="maroon"
            />
            <HeroStat
              testid="portal-overview-next-milestone"
              label={t("mile.next")}
              value={ov.next_milestone?.name || t("hero.noMilestoneSet", "No milestone set")}
              sub={ov.next_milestone?.target_date || "—"}
              accent="gold"
            />
            <HeroStat
              testid="portal-overview-target-date"
              label={t("hero.targetCompletion", "Target Completion")}
              value={targetDate || t("hero.notSet", "Not set")}
              sub={targetDate && days !== null ? (ov.overdue ? t("proj.card.daysOverdue", null, { n: days }) : t("proj.card.daysLeft", null, { n: days })) : "—"}
              accent={ov.overdue ? "red" : "charcoal"}
            />
            <HeroStat
              testid="portal-overview-pm"
              label={t("hero.pm")}
              value={pm?.name || t("hero.unassigned", "Unassigned")}
              sub={pm
                ? `${pm.email || ""}${ov.additional_project_managers?.length ? ` · +${ov.additional_project_managers.length} PM` : ""}`
                : (ov.site_engineer ? `${t("hero.siteEngineer")}: ${ov.site_engineer.name}` : t("hero.assignInTeam", "Assign in team & access"))}
              accent="charcoal"
            />
            <HeroStat
              testid="portal-overview-team"
              label={t("hero.teamMembers", "Team Members")}
              value={ov.team_count != null ? t("hero.onProject", null, { n: ov.team_count }) : "—"}
              sub={t("hero.includingClientSubs", "Including client & subs")}
              accent="maroon"
            />
            <HeroStat
              testid="portal-overview-latest-update"
              label={t("hero.latestUpdate", "Latest Site Update")}
              value={
                ov.latest_update?.work_completed
                  ? ov.latest_update.work_completed.slice(0, 60) +
                    (ov.latest_update.work_completed.length > 60 ? "…" : "")
                  : t("daily.empty")
              }
              sub={
                ov.latest_update
                  ? `${ov.latest_update.date}${ov.latest_update_author ? ` · ${ov.latest_update_author.name}` : ""}`
                  : "—"
              }
              accent="gold"
            />
          </div>
        </div>

        {/* Phase stepper */}
        <WorkstreamStatusPanel ws={ov.workstreams} slug={slug} canManage={canManage} isAdmin={isAdmin} />
        <PhaseStepper phases={ov.phases || []} currentId={ov.current_phase?.id} />

        {showBreakdown && (
          <ProgressBreakdownModal slug={slug} onClose={() => setShowBreakdown(false)} />
        )}

        {/* Bottom row — Latest photos + Risk chips */}
        {(ov.latest_photos.length > 0 || ov.delayed_tasks.length > 0 || ov.high_risk_tasks.length > 0) && (
          <div className="mt-6 pt-5 border-t border-[#6E1F2C]/10 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-5 items-start">
            {(ov.delayed_tasks.length > 0 || ov.high_risk_tasks.length > 0) ? (
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#A3141A] font-medium mb-2.5">
                  {t("hero.riskAlerts")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {ov.delayed_tasks.map((task) => (
                    <span
                      key={task.id}
                      data-testid={`portal-overview-delayed-${task.id}`}
                      className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase bg-[#A3141A] text-white px-2 py-1"
                    >
                      <AlertCircle size={10} /> {t("status.delayed")}: {task.name}
                    </span>
                  ))}
                  {ov.high_risk_tasks.slice(0, 4).map((task) => (
                    <span
                      key={task.id}
                      data-testid={`portal-overview-risk-${task.id}`}
                      className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase border border-[#A3141A]/40 text-[#A3141A] px-2 py-1 bg-white"
                    >
                      <AlertCircle size={10} /> {t("status.atRisk")}: {task.name}
                    </span>
                  ))}
                </div>
              </div>
            ) : <div />}

            {ov.latest_photos.length > 0 && (
              <div className="md:justify-self-end">
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium mb-2.5 md:text-right">
                  {t("hero.recentPhotos", "Recent Photos")}
                </p>
                <div className="flex gap-2">
                  {ov.latest_photos.slice(0, 3).map((ph) => (
                    <img
                      key={ph.id}
                      data-testid={`portal-overview-photo-${ph.id}`}
                      src={ph.url || portalApi.attachmentUrl(ph.id)}
                      alt=""
                      className="w-20 h-20 object-cover border border-[#6E1F2C]/15 hover:border-[#6E1F2C] transition-all"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


const VIS_LABEL = {
  public: "Public · Everyone on this project",
  internal: "Internal · Time Furniture team only",
  client: "Client · Time Furniture + Client + Consultant",
  subcontractor: "Subcontractor · Time Furniture + Subcontractor",
};

// Client-side contributor roles — may post daily updates, reply on updates
// and upload non-financial documents (mirrors backend CLIENT_SIDE_ROLES).
const CLIENT_CONTRIB_ROLES = [
  "client", "client_project_manager", "client_interior_designer",
  "client_associate", "client_subcontractor", "client_supplier",
];

const STATUS_LABEL = {
  planning: "Planning", active: "Active", delayed: "Delayed",
  awaiting_client: "Awaiting Client", completed: "Completed", handover: "Handover",
};
const STATUS_CLASS = {
  planning: "bg-[#7A6B5F] text-white",
  active: "bg-[#6E1F2C] text-white",
  delayed: "bg-[#A3141A] text-white",
  awaiting_client: "bg-[#D4A963] text-[#1C110C]",
  completed: "bg-[#1C110C] text-white",
  handover: "bg-[#56151F] text-white",
};

function Section({ title, action, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white border border-[#6E1F2C]/15 mb-6">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#6E1F2C]/10">
        <h2 className="font-serif-display text-lg text-[#1C110C]">{title}</h2>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function DailyUpdateForm({ slug, onPosted, isAdmin, isClientSide }) {
  const [text, setText] = useState("");
  const [updateDate, setUpdateDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [photos, setPhotos] = useState([]);
  const [docs, setDocs] = useState([]); // non-image attachments {id, filename, size, content_type}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [extras, setExtras] = useState({
    manpower: "", subs_on_site: "", materials_delivered: "",
    delays: "", next_day_plan: "", progress_percent: "",
  });
  const [visibility, setVisibility] = useState("public");
  const [err, setErr] = useState("");
  // Optional programme link
  const [timeline, setTimeline] = useState(null);
  const [supplyOptions, setSupplyOptions] = useState([]);
  const [link, setLink] = useState({
    phase_id: "", task_id: "", milestone_id: "", subcontractor_scope: "",
    external_supply_id: "",
    task_status: "", task_progress_percent: "",
    milestone_status: "", milestone_progress_percent: "",
  });

  useEffect(() => {
    let alive = true;
    portalApi.getTimeline(slug).then((d) => { if (alive) setTimeline(d); }).catch(() => {});
    if (!isClientSide) {
      portalApi.externalSupplyOptions(slug)
        .then((d) => { if (alive) setSupplyOptions(d.options || []); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [slug, isClientSide]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setErr("");
    try {
      const images = files.filter((f) => f.type.startsWith("image/"));
      const others = files.filter((f) => !f.type.startsWith("image/"));
      const tooBig = others.filter((f) => f.size > 100 * 1024 * 1024);
      if (tooBig.length) {
        setErr(`${tooBig.map((f) => f.name).join(", ")} exceeds the 100 MB limit.`);
      }
      const okOthers = others.filter((f) => f.size <= 100 * 1024 * 1024);
      if (images.length) {
        const compressed = await Promise.all(images.map((f) => compressImage(f)));
        const { attachments } = await portalApi.upload(slug, compressed, "photo");
        setPhotos((p) => [...p, ...attachments.map((a) => ({ ...a, caption: "" }))]);
      }
      if (okOthers.length) {
        const { attachments } = await portalApi.upload(slug, okOthers, "file");
        setDocs((d) => [...d, ...attachments]);
      }
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const setPhotoCaption = (id, caption) =>
    setPhotos((p) => p.map((ph) => (ph.id === id ? { ...ph, caption } : ph)));

  const removePhoto = (id) =>
    setPhotos((p) => p.filter((ph) => ph.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        date: isAdmin ? (updateDate || today) : today,
        work_completed: text,
        manpower: extras.manpower ? Number(extras.manpower) : null,
        subs_on_site: extras.subs_on_site || null,
        materials_delivered: extras.materials_delivered || null,
        delays: extras.delays || null,
        next_day_plan: extras.next_day_plan || null,
        progress_percent: extras.progress_percent === "" ? null : Number(extras.progress_percent),
        visibility,
        photo_ids: photos.map((p) => p.id),
        file_ids: docs.map((d) => d.id),
      };
      if (link.phase_id) payload.phase_id = link.phase_id;
      if (link.task_id) payload.task_id = link.task_id;
      if (link.milestone_id) payload.milestone_id = link.milestone_id;
      if (link.external_supply_id) {
        payload.external_supply_id = link.external_supply_id;
        payload.link_kind = "vendor_package";
      }
      if (link.subcontractor_scope) payload.subcontractor_scope = link.subcontractor_scope;
      if (!isClientSide && link.task_id && link.task_status) payload.task_status = link.task_status;
      if (!isClientSide && link.task_id && link.task_progress_percent !== "") payload.task_progress_percent = Number(link.task_progress_percent);
      if (!isClientSide && link.milestone_id && link.milestone_status) payload.milestone_status = link.milestone_status;
      if (!isClientSide && link.milestone_id && link.milestone_progress_percent !== "") payload.milestone_progress_percent = Number(link.milestone_progress_percent);
      await portalApi.createUpdate(slug, payload);
      // Persist captions for any photo that has one.
      const captioned = photos.filter((p) => (p.caption || "").trim());
      if (captioned.length) {
        await Promise.all(
          captioned.map((p) => portalApi.updatePhotoCaption(p.id, p.caption.trim()).catch(() => null))
        );
      }
      setText(""); setPhotos([]); setDocs([]);
      setExtras({ manpower: "", subs_on_site: "", materials_delivered: "", delays: "", next_day_plan: "", progress_percent: "" });
      setLink({ phase_id: "", task_id: "", milestone_id: "", subcontractor_scope: "", external_supply_id: "", task_status: "", task_progress_percent: "", milestone_status: "", milestone_progress_percent: "" });
      onPosted();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // Tasks filtered by selected phase (if any), so the dropdown isn't overwhelming.
  const [phaseQuery, setPhaseQuery] = useState("");
  const [taskQuery, setTaskQuery] = useState("");
  const _uniqById = (arr) => {
    const seen = new Set(); const out = [];
    for (const x of arr || []) { if (x && !seen.has(x.id)) { seen.add(x.id); out.push(x); } }
    return out;
  };
  const uniquePhases = _uniqById(timeline?.phases).filter(
    (p) => !phaseQuery || (p.name || "").toLowerCase().includes(phaseQuery.toLowerCase())
  );
  const uniqueMilestones = _uniqById(timeline?.milestones);
  const suggestedMilestone = uniqueMilestones.find((m) => m.is_next_milestone);
  const visibleTasks = _uniqById(timeline?.tasks)
    .filter((t) => !link.phase_id || t.phase_id === link.phase_id)
    .filter((t) => !taskQuery || (t.name || "").toLowerCase().includes(taskQuery.toLowerCase()));

  return (
    <form onSubmit={submit} data-testid="portal-update-form" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">Update date</span>
        {isAdmin ? (
          <input
            type="date"
            data-testid="portal-update-date-input"
            value={updateDate}
            onChange={(e) => setUpdateDate(e.target.value)}
            className="bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-1.5 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
          />
        ) : (
          <span data-testid="portal-update-date-readonly" className="text-sm text-[#1C110C] bg-[#FAF5EC] border border-[#6E1F2C]/10 px-3 py-1.5">
            {new Date().toISOString().slice(0, 10)} (today)
          </span>
        )}
        {isAdmin && updateDate !== new Date().toISOString().slice(0, 10) && (
          <span className="text-[10px] text-[#A6822F]">Back-dated entry — admin only</span>
        )}
      </div>
      <textarea
        data-testid="portal-update-text"
        required
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What was completed today?"
        rows={4}
        className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C] resize-none"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { k: "manpower", t: "Manpower on site (number)", type: "number" },
          { k: "progress_percent", t: "Progress %", type: "number" },
          { k: "subs_on_site", t: "Subcontractors on site" },
          { k: "materials_delivered", t: "Materials delivered" },
          { k: "delays", t: "Delays / issues" },
          { k: "next_day_plan", t: "Planned for tomorrow" },
        ].map((f) => (
          <input
            key={f.k}
            data-testid={`portal-update-${f.k}`}
            type={f.type || "text"}
            value={extras[f.k]}
            onChange={(e) => setExtras((s) => ({ ...s, [f.k]: e.target.value }))}
            placeholder={f.t}
            className="bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-2.5 text-[#1C110C] text-sm focus:outline-none focus:border-[#6E1F2C]"
          />
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="cursor-pointer inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors">
          <Paperclip size={14} /> Add Attachments
          <input
            type="file"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.dwg,.dxf,.zip"
            multiple
            onChange={onPick}
            className="hidden"
            data-testid="portal-update-photos-input"
          />
        </label>
        {uploading && (
          <span className="text-xs text-[#7A6B5F] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Uploading…</span>
        )}
        {isClientSide ? (
          <span data-testid="portal-update-visibility-fixed" className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
            Visible to the Time Furniture team &amp; client side
          </span>
        ) : (
          <select
            value={visibility}
            data-testid="portal-update-visibility"
            onChange={(e) => setVisibility(e.target.value)}
            className="bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-xs text-[#1C110C]"
          >
            {Object.entries(VIS_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        )}
      </div>
      {docs.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="portal-update-doc-list">
          {docs.map((d) => (
            <li
              key={d.id}
              data-testid={`portal-update-doc-${d.id}`}
              className="inline-flex items-center gap-2 bg-[#FAF5EC] border border-[#6E1F2C]/20 px-2.5 py-1.5 text-xs text-[#3E2A22]"
            >
              <FileText size={11} className="text-[#6E1F2C]" />
              <span className="max-w-[200px] truncate" title={d.filename}>{d.filename}</span>
              <span className="text-[10px] text-[#7A6B5F]">{((d.size || 0) / 1024).toFixed(0)} KB</span>
              <button
                type="button"
                onClick={() => setDocs((p) => p.filter((x) => x.id !== d.id))}
                aria-label="Remove attachment"
                data-testid={`portal-update-doc-remove-${d.id}`}
                className="ml-1 text-[#7A6B5F] hover:text-[#A3141A]"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {photos.length > 0 && (
        <div data-testid="portal-update-photo-thumbs" className="space-y-3">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C]">
            Add a short caption under each photo (optional)
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p, idx) => (
              <div key={p.id} className="bg-[#FAF5EC] border border-[#6E1F2C]/15 p-2">
                <div className="relative">
                  <img
                    src={p.url || portalApi.attachmentUrl(p.id)}
                    alt={p.filename}
                    className="w-full h-28 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    data-testid={`portal-update-photo-remove-${p.id}`}
                    className="absolute top-1 right-1 bg-[#1C110C]/80 hover:bg-[#A3141A] text-[#FAF5EC] w-6 h-6 flex items-center justify-center text-xs"
                    title="Remove this photo"
                  >
                    ×
                  </button>
                </div>
                <input
                  type="text"
                  value={p.caption || ""}
                  onChange={(e) => setPhotoCaption(p.id, e.target.value)}
                  data-testid={`portal-update-photo-caption-${p.id}`}
                  placeholder={[
                    "Ceiling framing completed",
                    "Cable tray installed",
                    "Marble delivery received",
                    "MEP first fix in progress",
                    "Site cleaning completed",
                  ][idx % 5]}
                  maxLength={280}
                  className="w-full mt-2 bg-white border border-[#6E1F2C]/15 px-2 py-1.5 text-xs text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Optional programme link */}
      {(supplyOptions.length > 0 || (timeline && (timeline.phases.length > 0 || timeline.tasks.length > 0 || (timeline.milestones || []).length > 0))) && (
        <details data-testid="portal-update-link-section" className="border border-[#6E1F2C]/15 px-4 py-3 bg-[#FAF5EC]/40">
          <summary className="cursor-pointer text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C]">
            Link this update to… (optional)
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            {timeline && timeline.phases.length > 0 && (
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Phase / Workstream</label>
                {timeline.phases.length > 6 && (
                  <input
                    data-testid="portal-update-phase-search"
                    value={phaseQuery}
                    onChange={(e) => setPhaseQuery(e.target.value)}
                    placeholder="Search phase…"
                    className="w-full mb-1 bg-white border border-[#6E1F2C]/15 px-3 py-1.5 text-xs text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                  />
                )}
                <select
                  data-testid="portal-update-phase"
                  value={link.phase_id}
                  onChange={(e) => { setLink((l) => ({ ...l, phase_id: e.target.value, task_id: "" })); setTaskQuery(""); }}
                  className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                >
                  <option value="">— Any —</option>
                  {uniquePhases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
            {(link.phase_id ? true : visibleTasks.length > 0) && (
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Activity / Task</label>
                {(timeline?.tasks || []).length > 6 && (
                  <input
                    data-testid="portal-update-task-search"
                    value={taskQuery}
                    onChange={(e) => setTaskQuery(e.target.value)}
                    placeholder="Search activity…"
                    className="w-full mb-1 bg-white border border-[#6E1F2C]/15 px-3 py-1.5 text-xs text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                  />
                )}
                <select
                  data-testid="portal-update-task"
                  value={link.task_id}
                  onChange={(e) => setLink((l) => ({ ...l, task_id: e.target.value }))}
                  className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                >
                  <option value="">{link.phase_id ? "— None in this phase —" : "— None —"}</option>
                  {visibleTasks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            {uniqueMilestones.length > 0 && (
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Milestone</label>
                <select
                  data-testid="portal-update-milestone"
                  value={link.milestone_id}
                  onChange={(e) => setLink((l) => ({ ...l, milestone_id: e.target.value, milestone_status: "", milestone_progress_percent: "" }))}
                  className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                >
                  <option value="">— None —</option>
                  {uniqueMilestones.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.is_next_milestone ? "★ " : ""}{m.name}{m.status === "completed" ? " ✓" : ""}
                    </option>
                  ))}
                </select>
                {suggestedMilestone && link.milestone_id !== suggestedMilestone.id && (
                  <button
                    type="button"
                    data-testid="portal-update-milestone-suggestion"
                    onClick={() => setLink((l) => ({ ...l, milestone_id: suggestedMilestone.id, milestone_status: "", milestone_progress_percent: "" }))}
                    className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#A6822F] hover:text-[#6E1F2C]"
                  >
                    ★ Suggested: {suggestedMilestone.name} — use it
                  </button>
                )}
              </div>
            )}
            {supplyOptions.length > 0 && (
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Vendor package (external supply)</label>
                <select
                  data-testid="portal-update-vendor-package"
                  value={link.external_supply_id}
                  onChange={(e) => setLink((l) => ({ ...l, external_supply_id: e.target.value }))}
                  className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                >
                  <option value="">— None —</option>
                  {supplyOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.item_group || "Package"}{o.vendor_source ? ` · ${o.vendor_source}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Subcontractor / scope</label>
              <input
                data-testid="portal-update-scope"
                value={link.subcontractor_scope}
                onChange={(e) => setLink((l) => ({ ...l, subcontractor_scope: e.target.value }))}
                placeholder="Marble, MEP, Joinery…"
                className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
              />
            </div>
            {link.task_id && !isClientSide && (
              <>
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Update task status</label>
                  <select
                    data-testid="portal-update-task-status"
                    value={link.task_status}
                    onChange={(e) => setLink((l) => ({ ...l, task_status: e.target.value }))}
                    className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                  >
                    <option value="">— Keep as is —</option>
                    <option value="not_started">Not started</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                    <option value="delayed">Delayed</option>
                    <option value="on_hold">On hold</option>
                    <option value="awaiting_approval">Awaiting approval</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">Task progress %</label>
                  <input
                    data-testid="portal-update-task-progress"
                    type="number" min="0" max="100"
                    value={link.task_progress_percent}
                    onChange={(e) => setLink((l) => ({ ...l, task_progress_percent: e.target.value }))}
                    placeholder="e.g. 40"
                    className="w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                  />
                </div>
              </>
            )}
            {link.milestone_id && !isClientSide && (
              <>
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-[#A6822F] mb-1">★ Update milestone status</label>
                  <select
                    data-testid="portal-update-milestone-status"
                    value={link.milestone_status}
                    onChange={(e) => {
                      const v = e.target.value;
                      setLink((l) => ({
                        ...l,
                        milestone_status: v,
                        // Mirror the server rule: completed → progress 100.
                        milestone_progress_percent: v === "completed" ? "100" : l.milestone_progress_percent,
                      }));
                    }}
                    className="w-full bg-white border border-[#D4A963]/40 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#A6822F]"
                  >
                    <option value="">— Keep as is —</option>
                    <option value="planned">Planned</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                    <option value="delayed">Delayed</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-[#A6822F] mb-1">Milestone progress %</label>
                  <input
                    data-testid="portal-update-milestone-progress"
                    type="number" min="0" max="100"
                    value={link.milestone_progress_percent}
                    onChange={(e) => setLink((l) => ({ ...l, milestone_progress_percent: e.target.value }))}
                    placeholder="e.g. 60"
                    className="w-full bg-white border border-[#D4A963]/40 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#A6822F]"
                  />
                </div>
              </>
            )}
          </div>
        </details>
      )}

      {err && <p className="text-xs text-[#A3141A]">{err}</p>}
      <button
        type="submit"
        disabled={busy}
        data-testid="portal-update-submit"
        className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
      >
        <Send size={14} /> {busy ? "Posting…" : "Post Daily Update"}
      </button>
    </form>
  );
}

// Sub-set of MIME types accepted by the backend. Matches the file picker's
// `accept` attribute so the OS dialog only lets users pick valid files.
const COMMENT_ACCEPT =
  "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv";
const MAX_COMMENT_FILE_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_COMMENT_FILES = 5;

function CommentBox({ slug, onPosted, visibilityOptions }) {
  const [text, setText] = useState("");
  const [vis, setVis] = useState("public");
  const [files, setFiles] = useState([]); // File[]
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInputRef = React.useRef(null);

  const addFiles = (incoming) => {
    setErr("");
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_COMMENT_FILES) {
        setErr(`Up to ${MAX_COMMENT_FILES} attachments per comment.`);
        break;
      }
      if (f.size > MAX_COMMENT_FILE_BYTES) {
        setErr(`${f.name} is over 20 MB.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (i) => setFiles((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return;
    setErr(""); setBusy(true);
    try {
      // Client-side image compression (mirror photo upload pipeline) keeps
      // 10 MB camera shots under the 20 MB cap and saves bandwidth.
      const prepared = await Promise.all(
        files.map((f) => (f.type.startsWith("image/") ? compressImage(f) : Promise.resolve(f))),
      );
      await portalApi.createComment(slug, {
        body: text, visibility: vis, files: prepared,
      });
      setText("");
      setFiles([]);
      onPosted();
    } catch (ex) {
      setErr(ex?.message || "Failed to post comment");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="portal-comment-form" className="space-y-3">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="portal-comment-text"
        rows={3}
        placeholder="Write a comment…"
        className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C] resize-none"
      />
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="portal-comment-attach-list">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              data-testid={`portal-comment-attach-${i}`}
              className="inline-flex items-center gap-2 bg-[#FAF5EC] border border-[#6E1F2C]/20 px-2.5 py-1.5 text-xs text-[#3E2A22]"
            >
              <Paperclip size={11} className="text-[#6E1F2C]" />
              <span className="max-w-[180px] truncate" title={f.name}>{f.name}</span>
              <span className="text-[10px] text-[#7A6B5F]">{(f.size / 1024).toFixed(0)} KB</span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                aria-label="Remove attachment"
                data-testid={`portal-comment-attach-remove-${i}`}
                className="ml-1 text-[#7A6B5F] hover:text-[#A3141A]"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {err && (
        <p className="text-xs text-[#A3141A]" data-testid="portal-comment-error">{err}</p>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept={COMMENT_ACCEPT}
          multiple
          onChange={(e) => addFiles(Array.from(e.target.files || []))}
          data-testid="portal-comment-file-input"
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          data-testid="portal-comment-attach-btn"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] hover:text-[#56151F] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-1.5"
          title="Attach photos, PDF, Word, Excel (up to 20 MB each, 5 per comment)"
        >
          <Paperclip size={11} /> Attach
        </button>
        <select value={vis} data-testid="portal-comment-vis" onChange={(e) => setVis(e.target.value)} className="bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-xs">
          {Object.entries(VIS_LABEL)
            .filter(([v]) => !visibilityOptions || visibilityOptions.includes(v))
            .map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
        </select>
        <button type="submit" disabled={busy || (!text.trim() && !files.length)} data-testid="portal-comment-submit" className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />}
          {busy ? "Posting…" : "Post Comment"}
        </button>
      </div>
    </form>
  );
}


function CommentAttachments({ attachments }) {
  if (!attachments || !attachments.length) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-2" data-testid="portal-comment-attachments">
      {attachments.map((a) => {
        const isImg = (a.content_type || "").startsWith("image/");
        if (isImg) {
          return (
            <li key={a.id} className="relative" data-testid={`portal-comment-attach-img-${a.id}`}>
              <a href={a.url} target="_blank" rel="noopener noreferrer" title={a.filename}>
                <img
                  src={a.url}
                  alt={a.filename}
                  className="w-20 h-20 object-cover border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/40"
                  loading="lazy"
                />
              </a>
            </li>
          );
        }
        return (
          <li key={a.id} data-testid={`portal-comment-attach-doc-${a.id}`}>
            <a
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              download={a.filename}
              className="inline-flex items-center gap-2 bg-[#FAF5EC] border border-[#6E1F2C]/20 hover:border-[#6E1F2C] px-3 py-1.5 text-xs text-[#3E2A22]"
            >
              <FileText size={12} className="text-[#6E1F2C]" />
              <span className="max-w-[180px] truncate">{a.filename}</span>
              <Download size={11} className="text-[#7A6B5F]" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}


const GROUP_LABELS_PROJ = {
  client_side: "Client Side",
  contractor_side: "Contractor Side",
  external: "Subcontractors / Suppliers",
};

const ROLE_LABELS_PROJ = {
  admin: "Main Admin",
  project_manager: "Project Manager",
  site_engineer: "Site Engineer",
  admin_staff: "Admin Staff",
  client: "Client",
  consultant: "Client Consultant",
  client_project_manager: "Client Project Manager",
  client_interior_designer: "Client Interior Designer",
  client_associate: "Client Associate",
  client_subcontractor: "Client Subcontractor",
  client_supplier: "Client Supplier",
  subcontractor: "Subcontractor",
  supplier: "Supplier",
  delivery_team: "Delivery Team",
  housekeeping_team: "Housekeeping / Cleaning Team",
};

const GROUP_ROLES_PROJ = {
  client_side: ["client", "client_project_manager", "client_interior_designer", "client_associate", "client_subcontractor", "client_supplier"],
  contractor_side: ["project_manager", "site_engineer", "admin_staff"],
  external: ["subcontractor", "supplier", "delivery_team", "housekeeping_team"],
};

const SCOPE_REQUIRED_ROLES = new Set([
  "subcontractor", "supplier", "delivery_team", "housekeeping_team",
  "client_subcontractor", "client_supplier",
]);

function generateTempPassword() {
  const u = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const l = "abcdefghijkmnopqrstuvwxyz";
  const d = "23456789";
  const s = "!@#$%&*?";
  const all = u + l + d + s;
  const pick = (x) => x[Math.floor(Math.random() * x.length)];
  const parts = [pick(u), pick(l), pick(d), pick(s)];
  for (let i = parts.length; i < 14; i++) parts.push(pick(all));
  for (let i = parts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [parts[i], parts[j]] = [parts[j], parts[i]];
  }
  return parts.join("");
}

function isTestUser(u) {
  return !!u.is_test
    || /@example\.com$/i.test(u.email || "")
    || /^(qa\.|test\.|test\+|pm\.test|site\.test|client\.test|sara\.test|qa2a2)/i.test(u.email || "")
    || /\b(qa|test|demo)\b/i.test(u.name || "");
}

function AddMemberModal({ slug, open, onClose, onAdded, existingMemberIds }) {
  const [step, setStep] = useState(1);
  const [group, setGroup] = useState("client_side");
  const [role, setRole] = useState("client");
  const [mode, setMode] = useState("existing"); // 'existing' | 'invite'
  const [users, setUsers] = useState([]);
  const [showTest, setShowTest] = useState(false);
  // existing-user state
  const [pickedUserId, setPickedUserId] = useState("");
  // invite state
  const [invite, setInvite] = useState({
    name: "", email: "", company: "", phone: "",
    password: "", send_invite: true, include_password_in_email: true,
    daily_report_recipient: false, weekly_report_recipient: false,
    preferred_language: "en",
  });
  const [scope, setScope] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // Reset modal state when re-opened.
  useEffect(() => {
    if (!open) return;
    setStep(1); setGroup("client_side"); setRole("client"); setMode("existing");
    setPickedUserId(""); setScope(""); setErr(""); setOkMsg("");
    setInvite({
      name: "", email: "", company: "", phone: "",
      password: generateTempPassword(), send_invite: true, include_password_in_email: true,
      daily_report_recipient: false, weekly_report_recipient: false,
      preferred_language: "en",
    });
    portalApi.listUsers().then((d) => setUsers(d.users || [])).catch(() => {});
  }, [open]);

  if (!open) return null;

  // Pool of existing users matching the chosen group + role, excluding already-added members and (by default) QA/test users.
  const candidates = users.filter((u) =>
    GROUP_ROLES_PROJ[group].includes(u.role)
    && u.role === role
    && !existingMemberIds.has(u.id)
    && (showTest || !isTestUser(u))
  );

  const scopeRequired = SCOPE_REQUIRED_ROLES.has(role);
  const groupSetAndRole = (g) => {
    setGroup(g);
    setRole(GROUP_ROLES_PROJ[g][0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOkMsg(""); setBusy(true);
    try {
      let userId = pickedUserId;
      if (mode === "invite") {
        const data = await portalApi.createUser({
          name: invite.name, email: invite.email, role,
          password: invite.password,
          company: invite.company || null, phone: invite.phone || null,
          assigned_project_slug: slug,
          assigned_scope: scopeRequired ? (scope || null) : null,
          send_invite: invite.send_invite,
          include_password_in_email: invite.include_password_in_email,
          force_password_change: true,
          daily_report_recipient: invite.daily_report_recipient,
          weekly_report_recipient: invite.weekly_report_recipient,
          preferred_language: invite.preferred_language || "en",
        });
        userId = data.user.id;
        setOkMsg(`Invited ${invite.name}. Temporary password: ${invite.password}`);
      } else {
        await portalApi.addMember(slug, { user_id: userId, scope: scopeRequired ? (scope || null) : null });
        setOkMsg("Member added to project.");
      }
      onAdded();
      setTimeout(() => onClose(), 1200);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2.5 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";
  const lblCls = "block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1.5";

  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto">
      <form
        onSubmit={submit}
        data-testid="portal-add-member-modal"
        className="bg-white w-full max-w-[640px] border border-[#6E1F2C]/20 p-7 md:p-9 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-1">Step {step} of 3</p>
            <h3 className="font-serif-display text-2xl text-[#1C110C]">Add or invite team member</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]">✕</button>
        </div>

        {/* STEP 1 — Group */}
        {step === 1 && (
          <>
            <p className="text-sm text-[#3E2A22] mb-4">Select the user group.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {Object.entries(GROUP_LABELS_PROJ).map(([k, label]) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => { groupSetAndRole(k); setStep(2); }}
                  data-testid={`portal-add-member-group-${k}`}
                  className="border border-[#6E1F2C]/25 px-3 py-4 text-sm text-[#1C110C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* STEP 2 — Role */}
        {step === 2 && (
          <>
            <p className="text-sm text-[#3E2A22] mb-4">Select the role within <strong>{GROUP_LABELS_PROJ[group]}</strong>.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {GROUP_ROLES_PROJ[group].map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => { setRole(r); setStep(3); }}
                  data-testid={`portal-add-member-role-${r}`}
                  className="border border-[#6E1F2C]/25 px-3 py-3 text-sm text-[#1C110C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors text-left"
                >
                  {ROLE_LABELS_PROJ[r]}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setStep(1)} className="mt-5 text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C]">← Back</button>
          </>
        )}

        {/* STEP 3 — Pick existing or invite */}
        {step === 3 && (
          <>
            <div className="text-xs text-[#7A6B5F] tracking-[0.22em] uppercase mb-3">
              {GROUP_LABELS_PROJ[group]} · <span className="text-[#6E1F2C]">{ROLE_LABELS_PROJ[role]}</span>
            </div>
            <div className="flex gap-2 mb-4">
              <button type="button" onClick={() => setMode("existing")} data-testid="portal-add-member-mode-existing"
                className={`flex-1 px-3 py-2 text-[10px] tracking-[0.22em] uppercase border ${mode === "existing" ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]" : "border-[#6E1F2C]/25 text-[#3E2A22]"}`}>
                Select existing user
              </button>
              <button type="button" onClick={() => setMode("invite")} data-testid="portal-add-member-mode-invite"
                className={`flex-1 px-3 py-2 text-[10px] tracking-[0.22em] uppercase border ${mode === "invite" ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]" : "border-[#6E1F2C]/25 text-[#3E2A22]"}`}>
                Invite new user
              </button>
            </div>

            {mode === "existing" ? (
              <>
                <label className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#3E2A22] cursor-pointer mb-3">
                  <input type="checkbox" data-testid="portal-add-member-show-test" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} className="accent-[#6E1F2C]" />
                  Show test users
                </label>
                {candidates.length === 0 ? (
                  <p className="text-xs text-[#7A6B5F] italic">No matching users found. Switch to <button type="button" className="underline" onClick={() => setMode("invite")}>Invite new user</button>.</p>
                ) : (
                  <>
                    <label className={lblCls}>Select {ROLE_LABELS_PROJ[role]}</label>
                    <select required value={pickedUserId} onChange={(e) => setPickedUserId(e.target.value)} data-testid="portal-add-member-existing-select" className={inputCls}>
                      <option value="">— Choose —</option>
                      {candidates.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}{u.company ? ` · ${u.company}` : ""} · {u.email}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={lblCls}>Full name</label><input data-testid="portal-add-member-invite-name" required value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className={inputCls} /></div>
                <div><label className={lblCls}>Email</label><input data-testid="portal-add-member-invite-email" type="email" required value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className={inputCls} /></div>
                <div><label className={lblCls}>Company</label><input data-testid="portal-add-member-invite-company" value={invite.company} onChange={(e) => setInvite({ ...invite, company: e.target.value })} className={inputCls} /></div>
                <div><label className={lblCls}>Phone</label><input data-testid="portal-add-member-invite-phone" value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} className={inputCls} placeholder="+971-50-…" /></div>
                <div className="sm:col-span-2">
                  <label className={lblCls}>Preferred language</label>
                  <select
                    data-testid="portal-add-member-invite-language"
                    value={invite.preferred_language}
                    onChange={(e) => setInvite({ ...invite, preferred_language: e.target.value })}
                    className={inputCls}
                  >
                    <option value="en">English</option>
                    <option value="ko">한국어 (Korean)</option>
                    <option value="ar">العربية (Arabic)</option>
                    <option value="zh">中文 (Mandarin)</option>
                  </select>
                  <p className="text-[10px] text-[#7A6B5F] mt-1">Used for portal labels and invitation/report emails for this user.</p>
                </div>
                <div className="sm:col-span-2">
                  <label className={lblCls}>Temporary password</label>
                  <div className="flex gap-2">
                    <input data-testid="portal-add-member-invite-password" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} className={`${inputCls} font-mono`} />
                    <button type="button" onClick={() => setInvite({ ...invite, password: generateTempPassword() })} className="px-3 border border-[#6E1F2C]/25 text-[10px] tracking-[0.2em] uppercase text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC]">Regen</button>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#1C110C] cursor-pointer">
                  <input type="checkbox" data-testid="portal-add-member-invite-send" checked={invite.send_invite} onChange={(e) => setInvite({ ...invite, send_invite: e.target.checked })} className="accent-[#6E1F2C]" />
                  Send invite email
                </label>
                {invite.send_invite && (
                  <label className="flex items-center gap-2 text-sm text-[#1C110C] cursor-pointer pl-4">
                    <input type="checkbox" checked={invite.include_password_in_email} onChange={(e) => setInvite({ ...invite, include_password_in_email: e.target.checked })} className="accent-[#6E1F2C]" />
                    Include password
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm text-[#1C110C] cursor-pointer">
                  <input type="checkbox" checked={invite.daily_report_recipient} onChange={(e) => setInvite({ ...invite, daily_report_recipient: e.target.checked })} className="accent-[#6E1F2C]" />
                  Daily report recipient
                </label>
                <label className="flex items-center gap-2 text-sm text-[#1C110C] cursor-pointer">
                  <input type="checkbox" checked={invite.weekly_report_recipient} onChange={(e) => setInvite({ ...invite, weekly_report_recipient: e.target.checked })} className="accent-[#6E1F2C]" />
                  Weekly report recipient
                </label>
              </div>
            )}

            {scopeRequired && (
              <div className="mt-4">
                <label className={lblCls}>Assigned scope / package</label>
                <input data-testid="portal-add-member-scope" value={scope} onChange={(e) => setScope(e.target.value)} className={inputCls} placeholder="Marble, Joinery, MEP, Delivery, Cleaning…" />
              </div>
            )}

            {err && <p data-testid="portal-add-member-error" className="text-xs text-[#A3141A] mt-4">{err}</p>}
            {okMsg && <p className="text-xs text-[#3F6E2C] mt-4 break-all">{okMsg}</p>}

            <div className="flex gap-3 mt-6">
              <button type="button" onClick={() => setStep(2)} className="flex-1 border border-[#6E1F2C]/30 text-[#6E1F2C] px-4 py-3 text-[10px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC]">← Back</button>
              <button
                type="submit"
                disabled={busy || (mode === "existing" && !pickedUserId) || (mode === "invite" && (!invite.name || !invite.email || !invite.password))}
                data-testid="portal-add-member-submit"
                className="flex-1 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-3 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-50"
              >
                {busy ? "Adding…" : mode === "invite" ? "Invite & add" : "Add to project"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

function EditMemberModal({ slug, member, onClose, onSaved, isAdmin }) {
  const u = member.user || {};
  const isAdminMember = member.role_on_project === "admin";
  const finEligible = !!member.finance_eligible;
  const initialGroup = Object.keys(GROUP_ROLES_PROJ).find((g) => GROUP_ROLES_PROJ[g].includes(member.role_on_project)) || "external";
  const [group, setGroup] = useState(initialGroup);
  const [role, setRole] = useState(member.role_on_project);
  const [scope, setScope] = useState(member.scope || "");
  const [daily, setDaily] = useState(!!u.daily_report_recipient);
  const [weekly, setWeekly] = useState(!!u.weekly_report_recipient);
  const [canFin, setCanFin] = useState(!!member.can_view_financials);
  const [rcvFin, setRcvFin] = useState(!!member.receive_financial_reports);
  const [status, setStatus] = useState(u.status === "disabled" ? "disabled" : "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputCls = "mt-1 w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-xs focus:outline-none focus:border-[#6E1F2C]";

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await portalApi.patchMember(slug, u.id, {
        ...(isAdminMember ? {} : { role_on_project: role }),
        scope: scope || null,
        daily_report_recipient: daily,
        weekly_report_recipient: weekly,
        ...(isAdmin && finEligible ? { can_view_financials: canFin, receive_financial_reports: rcvFin && canFin } : {}),
        status,
      });
      onSaved();
      onClose();
    } catch (ex) { setErr(ex.message || "Save failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="portal-member-edit-modal">
      <form onSubmit={save} className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-lg text-[#1C110C]">Edit team member</h3>
          <button type="button" onClick={onClose} data-testid="portal-member-edit-close"><X size={16} /></button>
        </div>
        <p className="text-xs text-[#3E2A22]">{u.name} <span className="text-[#7A6B5F]">· {u.email}</span></p>
        {isAdminMember ? (
          <div className="block" data-testid="portal-member-edit-admin-role">
            <span className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">Role on project</span>
            <p className="mt-1 bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-xs text-[#3E2A22]">
              Main Admin <span className="text-[#7A6B5F]">· role cannot be changed</span>
            </p>
          </div>
        ) : (
          <>
            <label className="block">
              <span className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">Group</span>
              <select value={group} onChange={(e) => { setGroup(e.target.value); setRole(GROUP_ROLES_PROJ[e.target.value][0]); }} className={inputCls} data-testid="portal-member-edit-group">
                {Object.keys(GROUP_ROLES_PROJ).map((g) => <option key={g} value={g}>{GROUP_LABELS_PROJ[g]}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">Role on project</span>
              <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} data-testid="portal-member-edit-role">
                {GROUP_ROLES_PROJ[group].map((r) => <option key={r} value={r}>{ROLE_LABELS_PROJ[r] || r}</option>)}
              </select>
            </label>
          </>
        )}
        <label className="block">
          <span className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">Scope (for subcontractors / suppliers)</span>
          <input value={scope} onChange={(e) => setScope(e.target.value)} className={inputCls} data-testid="portal-member-edit-scope" />
        </label>
        <div className="flex items-center gap-5 text-xs text-[#3E2A22]">
          <label className="flex items-center gap-2"><input type="checkbox" checked={daily} onChange={(e) => setDaily(e.target.checked)} data-testid="portal-member-edit-daily" /> Daily report</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={weekly} onChange={(e) => setWeekly(e.target.checked)} data-testid="portal-member-edit-weekly" /> Weekly report</label>
        </div>
        {/* Financial toggles — Main Admin only, and ONLY for finance-eligible roles
            (owner policy: admin, admin staff, client, client project manager). */}
        {isAdmin && !isAdminMember && !finEligible && (
          <div className="border border-[#7A6B5F]/30 bg-[#F4EFE8] p-3" data-testid="portal-member-finance-not-eligible">
            <p className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">Financial access — Not eligible</p>
            <p className="text-[10px] text-[#7A6B5F] mt-1">
              By owner policy, financial visibility is limited to Main Admin, internal admin staff,
              the Client and the Client Project Manager. This role cannot be granted financial access.
            </p>
          </div>
        )}
        {isAdmin && !isAdminMember && finEligible && (
          <div className="border border-[#C8A75D]/60 bg-[#FAF5EC] p-3 space-y-2">
            <p className="text-[9px] tracking-[0.24em] uppercase text-[#6E1F2C]">Financial access · Main Admin only</p>
            <label className="flex items-start gap-2 text-xs text-[#3E2A22] cursor-pointer">
              <input
                type="checkbox"
                checked={canFin}
                onChange={(e) => { setCanFin(e.target.checked); if (!e.target.checked) setRcvFin(false); }}
                data-testid="portal-member-edit-can-view-financials"
                className="accent-[#6E1F2C] mt-0.5"
              />
              <span>
                <strong>View financials</strong>
                <span className="block text-[10px] text-[#7A6B5F]">
                  Access to payment milestones, contract/main-works values, invoices, paid/balance, supplier amounts.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs text-[#3E2A22] cursor-pointer">
              <input
                type="checkbox"
                checked={rcvFin}
                disabled={!canFin}
                onChange={(e) => setRcvFin(e.target.checked)}
                data-testid="portal-member-edit-receive-financial-reports"
                className="accent-[#6E1F2C] mt-0.5 disabled:opacity-40"
              />
              <span>
                <strong>Receive financial reports</strong>
                <span className="block text-[10px] text-[#7A6B5F]">
                  Included when Main Admin sends a daily/weekly report that contains financial sections. Requires &quot;View financials&quot;.
                </span>
              </span>
            </label>
          </div>
        )}
        <label className="block">
          <span className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">Account status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="portal-member-edit-status">
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
        </label>
        {err && <p className="text-xs text-[#A3141A]" data-testid="portal-member-edit-error">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-[#6E1F2C]/30 text-[#6E1F2C] px-4 py-2 text-[10px] tracking-[0.24em] uppercase">Cancel</button>
          <button type="submit" disabled={busy} data-testid="portal-member-edit-save"
            className="bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2 text-[10px] tracking-[0.24em] uppercase disabled:opacity-60">Save</button>
        </div>
      </form>
    </div>
  );
}

function TeamAccessSection({ slug, members, canManage, isAdmin, onChanged }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const existingMemberIds = new Set((members || []).map((m) => m.user?.id).filter(Boolean));
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.user?.name || "this user"} from the project?`)) return;
    try { await portalApi.removeMember(slug, m.user.id); onChanged(); }
    catch (e) { alert(e.message); }
  };
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#3E2A22]">
          {(members || []).length} team member{(members || []).length === 1 ? "" : "s"} on this project
        </p>
        {canManage && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="portal-add-member-btn"
            className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
          >
            <UserPlus size={13} /> Add / Invite User
          </button>
        )}
      </div>

      <div className="bg-white border border-[#6E1F2C]/15 overflow-x-auto" data-testid="portal-members-list">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF5EC] text-[10px] tracking-[0.18em] uppercase text-[#7A6B5F]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Group · Role</th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Scope</th>
              <th className="text-left px-4 py-3">Reports</th>
              {isAdmin && <th className="text-left px-4 py-3">Financials</th>}
              <th className="text-left px-4 py-3">Status</th>
              {canManage && <th className="text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {(members || []).length === 0 ? (
              <tr><td colSpan={canManage ? (isAdmin ? 8 : 7) : (isAdmin ? 7 : 6)} className="px-4 py-8 text-center text-xs text-[#7A6B5F] italic">No team members yet.</td></tr>
            ) : (members || []).map((m) => {
              const r = m.role_on_project;
              const u = m.user || {};
              const group = Object.keys(GROUP_ROLES_PROJ).find((g) => GROUP_ROLES_PROJ[g].includes(r)) || u.group || "external";
              const isMainAdminRow = r === "admin";
              return (
                <tr key={m.id} data-testid={`portal-member-row-${m.id}`} className="border-t border-[#6E1F2C]/10 hover:bg-[#FAF5EC]/40">
                  <td className="px-4 py-3">
                    <p className="text-[#1C110C]">{u.name || "—"}</p>
                    {u.email && <p className="text-[10px] text-[#7A6B5F]">{u.email}</p>}
                    {u.phone && <p className="text-[10px] text-[#7A6B5F]">{u.phone}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="block text-[9px] tracking-[0.18em] uppercase text-[#6E1F2C]">{GROUP_LABELS_PROJ[group]}</span>
                    <span className="text-[#3E2A22]">{ROLE_LABELS_PROJ[r] || r}</span>
                  </td>
                  <td className="px-4 py-3 text-[#3E2A22]">{u.company || "—"}</td>
                  <td className="px-4 py-3 text-[#3E2A22]">{m.scope || "—"}</td>
                  <td className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase">
                    <p className={u.daily_report_recipient ? "text-[#6E1F2C]" : "text-[#7A6B5F]/50"}>{u.daily_report_recipient ? "✓ Daily" : "— Daily"}</p>
                    <p className={u.weekly_report_recipient ? "text-[#6E1F2C]" : "text-[#7A6B5F]/50"}>{u.weekly_report_recipient ? "✓ Weekly" : "— Weekly"}</p>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase" data-testid={`portal-member-fin-${m.id}`}>
                      {isMainAdminRow ? (
                        <p className="text-[#6E1F2C]">✓ Full · Main Admin</p>
                      ) : !m.finance_eligible ? (
                        <p className="text-[#7A6B5F]/60" data-testid={`portal-member-fin-not-eligible-${m.id}`}>Not eligible</p>
                      ) : (
                        <>
                          <p className={m.can_view_financials ? "text-[#6E1F2C]" : "text-[#7A6B5F]/50"}>
                            {m.can_view_financials ? "✓ View" : "— View"}
                          </p>
                          <p className={m.receive_financial_reports ? "text-[#6E1F2C]" : "text-[#7A6B5F]/50"}>
                            {m.receive_financial_reports ? "✓ Fin reports" : "— Fin reports"}
                          </p>
                        </>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className="text-[9px] tracking-[0.2em] uppercase bg-[#6E1F2C] text-white px-2 py-0.5">{u.status || "—"}</span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setEditing(m)}
                        data-testid={`portal-member-edit-${m.id}`}
                        title="Edit role, reports & status"
                        className="text-[10px] tracking-[0.22em] uppercase border border-[#6E1F2C]/40 text-[#6E1F2C] px-3 py-1.5 hover:bg-[#6E1F2C] hover:text-white mr-2"
                      >Edit</button>
                      <button
                        type="button"
                        onClick={() => remove(m)}
                        data-testid={`portal-member-remove-${m.id}`}
                        title="Remove from project"
                        className="text-[10px] tracking-[0.22em] uppercase border border-[#A3141A]/40 text-[#A3141A] px-3 py-1.5 hover:bg-[#A3141A] hover:text-white"
                      >Remove</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AddMemberModal
        slug={slug}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdded={onChanged}
        existingMemberIds={existingMemberIds}
      />
      {editing && (
        <EditMemberModal
          slug={slug}
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={onChanged}
          isAdmin={isAdmin}
        />
      )}
    </>
  );
}

function UpdateThread({ slug, updateId, comments, tmap, allowVisibility, onPosted }) {
  const [text, setText] = useState("");
  const [vis, setVis] = useState("public");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setErr(""); setBusy(true);
    try {
      await portalApi.createComment(slug, {
        body: text.trim(), parent_type: "update", parent_id: updateId,
        visibility: allowVisibility ? vis : "public",
      });
      setText("");
      onPosted();
    } catch (ex) { setErr(ex?.message || "Failed to post reply"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`portal-update-thread-${updateId}`} className="border border-t-0 border-[#6E1F2C]/15 bg-[#FAF5EC]/60 px-5 py-4">
      {comments.length > 0 && (
        <ul className="space-y-2.5 mb-3" data-testid={`portal-update-replies-${updateId}`}>
          {comments.map((c) => (
            <li key={c.id} className="bg-white border-l-2 border-[#D4A963] px-3 py-2">
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#7A6B5F]">
                {c.author?.name || "—"}{c.author?.role ? ` · ${c.author.role.replace(/_/g, " ")}` : ""} · {new Date(c.created_at).toLocaleString()}
              </p>
              {c.body && (
                <TranslatedText
                  original={c.body}
                  translated={tmap ? tmap[`cmt:${c.id}`] : null}
                  className="text-sm text-[#1C110C] mt-0.5 whitespace-pre-wrap"
                />
              )}
              <CommentAttachments attachments={c.attachments} />
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="flex items-start gap-2 flex-wrap">
        <textarea
          id={`update-reply-${updateId}`}
          data-testid={`portal-update-reply-input-${updateId}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={1}
          placeholder="Reply to this update…"
          className="flex-1 min-w-[220px] bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C] resize-none"
        />
        {allowVisibility && (
          <select
            value={vis}
            data-testid={`portal-update-reply-vis-${updateId}`}
            onChange={(e) => setVis(e.target.value)}
            className="bg-white border border-[#6E1F2C]/20 px-2 py-2 text-xs text-[#1C110C]"
          >
            {Object.entries(VIS_LABEL).map(([v, l]) => (<option key={v} value={v}>{l.split(" · ")[0]}</option>))}
          </select>
        )}
        <button
          type="submit"
          disabled={busy || !text.trim()}
          data-testid={`portal-update-reply-submit-${updateId}`}
          className="inline-flex items-center gap-1.5 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <MessageSquare size={12} />} Reply
        </button>
      </form>
      {err && <p className="text-xs text-[#A3141A] mt-1" data-testid={`portal-update-reply-error-${updateId}`}>{err}</p>}
    </div>
  );
}

export default function ProjectPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const reportDeepLinkId = searchParams.get("report");
  const tabDeepLink = searchParams.get("tab");
  const { user } = usePortalAuth();
  const { t, lang } = usePortalLang();
  const [data, setData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [comments, setComments] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState("");
  const [errorStatus, setErrorStatus] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const initialTab = reportDeepLinkId
    ? "reports"
    : (["overview", "timeline", "milestones", "approvals", "updates", "photos", "reports", "commissioning", "financials", "documents"].includes(tabDeepLink) ? tabDeepLink : "overview");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [financialsAccess, setFinancialsAccess] = useState(null); // {can_view, can_edit, has_shared}
  const [ledgerAccess, setLedgerAccess] = useState(null); // {can_view, can_edit} — received-money ledger (role-based)
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [editProjectOpen, setEditProjectOpen] = useState(false);

  // Auto-translate update/comment content for non-English viewers (cached server-side)
  const translateEntries = useMemo(() => {
    const out = [];
    updates.forEach((u) => {
      if (u.work_completed) out.push({ key: `upd:${u.id}:work`, text: u.work_completed });
      if (u.delays) out.push({ key: `upd:${u.id}:delays`, text: u.delays });
      if (u.next_day_plan) out.push({ key: `upd:${u.id}:plan`, text: u.next_day_plan });
      if (u.materials_delivered) out.push({ key: `upd:${u.id}:materials`, text: u.materials_delivered });
    });
    comments.forEach((c) => {
      if (c.body) out.push({ key: `cmt:${c.id}`, text: c.body });
    });
    return out;
  }, [updates, comments]);
  const tmap = useContentTranslation(slug, lang, translateEntries);

  // Split comments into update-scoped reply threads vs project-level comments
  // so replies on a daily update never leak into the general project feed.
  const threadsByUpdate = useMemo(() => {
    const m = {};
    comments.forEach((c) => {
      if (c.parent_type === "update" && c.parent_id) (m[c.parent_id] = m[c.parent_id] || []).push(c);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.created_at < b.created_at ? -1 : 1)));
    return m;
  }, [comments]);
  const projectComments = useMemo(
    () => comments.filter((c) => !(c.parent_type === "update" && c.parent_id)),
    [comments]
  );

  useEffect(() => {
    setError(""); setErrorStatus(0);
    portalApi.getProject(slug).then((d) => setData(d)).catch((e) => {
      setError(e.message); setErrorStatus(e.status || 0);
    });
  }, [slug, refreshKey]);

  useEffect(() => {
    if (!data) return;
    portalApi.listUpdates(slug).then((d) => setUpdates(d.updates || [])).catch(() => {});
    portalApi.listComments(slug).then((d) => setComments(d.comments || [])).catch(() => {});
    portalApi.getTimeline(slug).then(setTimeline).catch(() => setTimeline(null));
    // Probe financials access so we can decide whether to show the tab.
    // Strict rule: the Financials tab is hidden COMPLETELY for anyone who
    // cannot view financials. There is no fallback "Packages" tab — external
    // supply status for unauthorized viewers is rendered as an operational
    // widget elsewhere, never behind a Financials label.
    portalApi.getFinancials(slug)
      .then((f) => {
        const can_view = !!f.viewer_can_view;
        const can_edit = !!f.viewer_can_edit;
        setFinancialsAccess({ can_edit, can_view, has_shared: false });
      })
      .catch(() => setFinancialsAccess({ can_edit: false, can_view: false, has_shared: false }));
    // Separate received-money ledger: role-based (admin edit; client / client-PM /
    // PM read-only; every other role gets 403 and never sees the section).
    portalApi.getFinanceLedger(slug)
      .then((l) => setLedgerAccess({ can_view: !!l.viewer_can_view, can_edit: !!l.viewer_can_edit }))
      .catch(() => setLedgerAccess({ can_view: false, can_edit: false }));
  }, [data, slug, refreshKey]);

  // If a user lands on `?tab=financials` (deep-link or refresh) but does not
  // have permission, bounce them back to Overview so no financial view is
  // ever attempted for them.
  useEffect(() => {
    if (financialsAccess && ledgerAccess && !financialsAccess.can_view && !ledgerAccess.can_view && activeTab === "financials") {
      setActiveTab("overview");
    }
  }, [financialsAccess, ledgerAccess, activeTab]);

  if (error) {
    const isServerError = errorStatus >= 500;
    const title = isServerError ? "Project unavailable" : "Access denied";
    return (
      <PortalLayout title={title} breadcrumbs={[{ label: title }]}>
        <div className="bg-white border border-[#A3141A]/30 p-8 text-center">
          <AlertCircle className="mx-auto text-[#A3141A] mb-3" />
          <p className="text-sm text-[#1C110C]">{error}</p>
          <Link to="/projects" className="mt-5 inline-block text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">
            ← Back to projects
          </Link>
        </div>
      </PortalLayout>
    );
  }
  if (!data) {
    return (
      <PortalLayout>
        <p className="text-sm text-[#7A6B5F]">Loading project…</p>
      </PortalLayout>
    );
  }

  const { project, member, members } = data;
  const role = member.role_on_project;
  const isClientSide = CLIENT_CONTRIB_ROLES.includes(role);
  const canPostUpdate = ["admin", "project_manager", "site_engineer"].includes(role) || isClientSide;
  const canReplyVisibility = ["admin", "project_manager", "site_engineer", "admin_staff"].includes(role);
  // Clamp the general comment composer's audience to what the server will
  // accept for this role (mirrors backend tier coercion in create_comment).
  const commentVisOptions = isClientSide || role === "consultant"
    ? ["public", "client"]
    : ["subcontractor", "supplier", "delivery_team", "housekeeping_team"].includes(role)
      ? ["public", "subcontractor"]
      : null; // internal roles: all options
  const canManage = ["admin", "project_manager"].includes(role);
  // Admin-only actions: edit project master details, team CRUD, financial toggles,
  // Gantt/milestone/financial imports, milestone/approval edits.
  const isAdmin = role === "admin";

  return (
    <PortalLayout breadcrumbs={[{ label: t("nav.projects"), to: "/projects" }, { label: project.name }]}>
      {/* Header card */}
      <div className="bg-white border border-[#6E1F2C]/15 p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <span className={`text-[9px] tracking-[0.28em] uppercase px-2 py-1 ${STATUS_CLASS[project.status] || "bg-[#7A6B5F] text-white"}`}>
            {t(`status.${project.status === "awaiting_client" ? "awaitingApproval" : project.status}`, STATUS_LABEL[project.status] || project.status)}
          </span>
          <div className="flex items-center gap-3">
            {project.code && <span className="text-[10px] text-[#7A6B5F] tracking-wider">{project.code}</span>}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setEditProjectOpen(true)}
                data-testid="portal-edit-project-btn"
                className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] hover:text-[#56151F] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-1.5"
              >
                Edit details
              </button>
            )}
            {user?.role === "admin" && !user?.impersonating && (
              <PreviewAsButton slug={slug} projectName={project.name} />
            )}
          </div>
        </div>
        <h1 className="font-serif-display text-3xl md:text-4xl text-[#1C110C] tracking-tight">{project.name}</h1>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-[#3E2A22]">
          {project.client_name && <p><span className="text-[#7A6B5F] tracking-[0.2em] uppercase text-[10px] mr-2">{t("hero.client")}</span>{project.client_name}</p>}
          {project.location && <p><span className="text-[#7A6B5F] tracking-[0.2em] uppercase text-[10px] mr-2">{t("hero.location")}</span>{project.location}</p>}
          {project.project_type && <p><span className="text-[#7A6B5F] tracking-[0.2em] uppercase text-[10px] mr-2">{t("hero.type")}</span>{project.project_type}</p>}
        </div>
        <div className="mt-6">
          <div className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F] mb-1">
            <span>{t("proj.card.progress")}</span><span>{project.progress_percent || 0}%</span>
          </div>
          <div className="h-2 bg-[#FAF5EC]">
            <div className="h-full bg-[#D4A963]" style={{ width: `${Math.max(0, Math.min(100, project.progress_percent || 0))}%` }} />
          </div>
        </div>
      </div>

      {/* Tab strip */}
      <div className="border-b border-[#6E1F2C]/15 mb-7" data-testid="portal-project-tabs">
        <div className="flex gap-1 overflow-x-auto -mx-1 px-1">
          {[
            { k: "overview", label: t("tab.overview"), icon: LayoutDashboard },
            { k: "timeline", label: t("tab.timeline"), icon: BarChart3 },
            { k: "milestones", label: t("tab.milestones"), icon: Flag },
            { k: "approvals", label: t("tab.approvals"), icon: Landmark },
            { k: "updates", label: t("tab.updates"), icon: ClipboardList },
            { k: "photos", label: t("tab.photos"), icon: ImageIcon },
            { k: "reports", label: t("tab.reports"), icon: FileText },
            { k: "documents", label: t("tab.files"), icon: FileBox },
            { k: "commissioning", label: "Commissioning", icon: Gauge },
            ...((financialsAccess && financialsAccess.can_view) || (ledgerAccess && ledgerAccess.can_view)
              ? [{ k: "financials", label: t("tab.financials"), icon: DollarSign }]
              : []),
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.k;
            return (
              <button
                key={tab.k}
                onClick={() => setActiveTab(tab.k)}
                data-testid={`portal-tab-${tab.k}`}
                className={`inline-flex items-center gap-2 px-4 md:px-5 py-3 text-[10px] tracking-[0.24em] uppercase border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  active
                    ? "border-[#6E1F2C] text-[#6E1F2C]"
                    : "border-transparent text-[#7A6B5F] hover:text-[#1C110C]"
                }`}
              >
                <Icon size={13} /> {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === "timeline" && (
        <TimelineTab slug={slug} user={user} project={project} />
      )}

      {activeTab === "milestones" && (
        <MilestonesTab
          slug={slug}
          canManage={isAdmin}
          isAdmin={isAdmin}
        />
      )}

      {activeTab === "financials" && ((financialsAccess && financialsAccess.can_view) || (ledgerAccess && ledgerAccess.can_view)) && (
        <>
          {ledgerAccess && ledgerAccess.can_view && <FinanceLedgerSection slug={slug} />}
          {financialsAccess && financialsAccess.can_view && <FinancialsTab slug={slug} />}
        </>
      )}

      {activeTab === "documents" && (
        <KeyDocumentsTab
          slug={slug}
          canAddUpdate={canPostUpdate}
          onAddComment={() => {
            setActiveTab("overview");
            setTimeout(() => document.querySelector('[data-testid="portal-comment-text"]')?.focus(), 100);
          }}
          onAddUpdate={() => {
            setActiveTab("updates");
            setTimeout(() => document.getElementById("post-update")?.scrollIntoView({ behavior: "smooth" }), 50);
          }}
        />
      )}

      {activeTab === "commissioning" && (
        <CommissioningTab slug={slug} />
      )}

      {activeTab === "approvals" && (
        <ApprovalsTab slug={slug} canManage={isAdmin} isAdmin={isAdmin} />
      )}

      {activeTab === "updates" && (
        <div data-testid="portal-updates-tab" className="space-y-7 pb-24 lg:pb-0">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Site Diary</p>
              <h2 className="font-serif-display text-2xl text-[#1C110C]">Daily updates</h2>
            </div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F]">
              {updates.length} update{updates.length === 1 ? "" : "s"}
            </p>
          </div>

          {canPostUpdate && (
            <div id="post-update" className="bg-white border border-[#6E1F2C]/15 p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="h-px w-6 bg-[#D4A963]" />
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium">
                  {isClientSide ? "Share an update with the project team" : "Post a new daily update"}
                </p>
              </div>
              <DailyUpdateForm slug={slug} onPosted={() => setRefreshKey((k) => k + 1)} isAdmin={role === "admin"} isClientSide={isClientSide} />
            </div>
          )}

          {updates.length === 0 ? (
            <div className="bg-white border border-dashed border-[#6E1F2C]/25 p-12 text-center">
              <ClipboardList className="mx-auto text-[#D4A963] mb-3" size={36} />
              <p className="text-sm text-[#3E2A22]">No daily updates posted yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5" data-testid="portal-updates-list">
              {updates.map((u) => (
                <div key={u.id}>
                  <DailyUpdateCard
                    update={u}
                    timeline={timeline}
                    tmap={tmap}
                    commentCount={(threadsByUpdate[u.id] || []).length}
                    onComment={() => document.getElementById(`update-reply-${u.id}`)?.focus()}
                    canEdit={isAdmin}
                    onEdit={(upd) => setEditingUpdate(upd)}
                  />
                  <UpdateThread
                    slug={slug}
                    updateId={u.id}
                    comments={threadsByUpdate[u.id] || []}
                    tmap={tmap}
                    allowVisibility={canReplyVisibility}
                    onPosted={() => setRefreshKey((k) => k + 1)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "photos" && (
        <div className="pb-24 lg:pb-0">
          <PhotosTab
            updates={updates}
            timeline={timeline}
            currentUser={user}
            onCaptionSaved={() => setRefreshKey((k) => k + 1)}
          />
        </div>
      )}

      {activeTab === "reports" && (
        <div className="pb-24 lg:pb-0">
          <ReportsTab slug={slug} canManage={canManage} autoOpenReportId={reportDeepLinkId} isAdmin={user?.role === "admin"} />
        </div>
      )}

      {activeTab === "overview" && <>

      <ProjectOverviewHero slug={slug} project={data} refreshKey={refreshKey} canManage={canManage} isAdmin={isAdmin} />
      <ProgressSummaryCard slug={slug} isAdmin={role === "admin"} key={`ps-${refreshKey}`} onChanged={() => setRefreshKey((k) => k + 1)} />

      <KeyDocumentsCard slug={slug} onOpenTab={() => setActiveTab("documents")} />

      {updates.length > 0 && (
        <Section
          title={`Recent updates (${updates.length})`}
          testid="portal-section-updates"
          action={
            <button
              type="button"
              onClick={() => setActiveTab("updates")}
              data-testid="portal-overview-see-all-updates"
              className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] hover:text-[#56151F]"
            >
              See all →
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-5">
            {updates.slice(0, 3).map((u) => (
              <DailyUpdateCard
                key={u.id}
                update={u}
                timeline={timeline}
                tmap={tmap}
                canEdit={isAdmin}
                onEdit={(upd) => { setEditingUpdate(upd); setActiveTab("updates"); }}
              />
            ))}
          </div>
        </Section>
      )}

      <Section title={`Comments (${projectComments.length})`} testid="portal-section-comments">
        <CommentBox slug={slug} onPosted={() => setRefreshKey((k) => k + 1)} visibilityOptions={commentVisOptions} />
        {projectComments.length > 0 && (
          <ul className="mt-6 space-y-4" data-testid="portal-comments-list">
            {projectComments.map((c) => (
              <li key={c.id} className="bg-[#FAF5EC] border-l-2 border-[#6E1F2C]/40 px-4 py-3">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">
                  {c.author?.name} · {c.author?.role} · {new Date(c.created_at).toLocaleString()} · {VIS_LABEL[c.visibility]?.split(" · ")[0]}
                </p>
                {c.body && (
                  <TranslatedText
                    original={c.body}
                    translated={tmap[`cmt:${c.id}`]}
                    className="text-sm text-[#1C110C] mt-1 whitespace-pre-wrap"
                  />
                )}
                <CommentAttachments attachments={c.attachments} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {canManage && (
        <Section title="Team & access" testid="portal-section-team">
          <TeamAccessSection
            slug={slug}
            members={members}
            canManage={isAdmin}
            isAdmin={isAdmin}
            onChanged={() => setRefreshKey((k) => k + 1)}
          />
        </Section>
      )}
      </>}

      {canPostUpdate && (
        <MobileQuickActions
          items={[
            { key: "update", label: "Update", icon: ClipboardList, onClick: () => { setActiveTab("updates"); setTimeout(() => document.getElementById("post-update")?.scrollIntoView({ behavior: "smooth" }), 50); } },
            { key: "photos", label: "Photos", icon: Camera, onClick: () => { setActiveTab("updates"); setTimeout(() => document.querySelector('[data-testid="portal-update-photos-input"]')?.click(), 50); } },
            { key: "gantt", label: "Gantt", icon: BarChart3, onClick: () => setActiveTab("timeline") },
            { key: "delays", label: "Delays", icon: AlertCircle, onClick: () => { setActiveTab("updates"); setTimeout(() => document.querySelector('[data-testid="portal-update-delays"]')?.focus(), 50); } },
            { key: "plan", label: "Plan", icon: Calendar, onClick: () => { setActiveTab("updates"); setTimeout(() => document.querySelector('[data-testid="portal-update-next_day_plan"]')?.focus(), 50); } },
            { key: "comment", label: "Comment", icon: MessageSquare, onClick: () => { setActiveTab("overview"); setTimeout(() => document.querySelector('[data-testid="portal-comment-text"]')?.focus(), 100); } },
          ]}
        />
      )}

      {editingUpdate && (
        <EditDailyUpdateModal
          slug={slug}
          update={editingUpdate}
          isAdmin={role === "admin"}
          onClose={() => setEditingUpdate(null)}
          onSaved={() => setRefreshKey((k) => k + 1)}
        />
      )}
      {editProjectOpen && (
        <EditProjectModal
          project={project}
          onClose={() => setEditProjectOpen(false)}
          onSaved={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </PortalLayout>
  );
}
